import Backbone from 'backbone'
import _ from 'lodash'
import widgetMetadata from './widget-metadata'
import formWidgetCommonControls from './form-widget-common-controls.hbs'
import appDisplay from 'scoreboard/components/app-display'
import disableControls from 'scoreboard/components/disable-controls'
import escapePreserveLineBreaks from 'scoreboard/components/escape-preserve-line-breaks'
import urlsToLinks from 'scoreboard/components/urls-to-links'
import FormActionType from 'scoreboard/forms/model/form-action-type'
const { SAVE_RECORD, SET_FIELD_VALUE } = FormActionType
import WidgetState from 'scoreboard/forms/model/widget-state'
const { DISABLED, HIDDEN } = WidgetState
import FormWidgetType from 'scoreboard/forms/model/form-widget-type'
const { FIELD, BUTTON, RECORD_LIST } = FormWidgetType
import { isFieldHiddenForUser } from 'scoreboard/forms/model/form'
import EvaluateStatementsActor from 'scoreboard/forms/actions/evaluate-statements-actor'
import { isSaveGuaranteed } from 'scoreboard/forms/model/form'

const WidgetsView = Backbone.View.extend({

	tagName: 'ul',

	className: 'widgets-view list',

	events: {
		'click .edit': 'onClickEdit',
		'click .delete': 'onClickDelete'
	},

	/**
	 * View displaying all the widgets on a form and common UI functionality
	 * and components (Edit/Delete buttons, drag handles)
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Form} options.model
	 *
	 * @param {FormWidget[]} options.widgets array of widgets
	 *
	 * @param {Object} [options.allowInput=true] indicates whether the widget
	 * is allowing data to be entered (true on Overview, false on Edit tab)
	 *
	 * @param {FormView} options.formView the FormView this form is
	 * participating in
	 *
	 * @param {FormDirector} options.formDirector
	 *
	 * @param {Object<Number,Object>} options.treeItemsById is a hash where keys
	 * are tree IDs and values are tree items
	 */
	initialize: function(options = {}) {
		this.options = options
		this.formView = options.formView
		this.allowInput = _.get(options, 'allowInput', true)

		this.pageOrder = _.map(this.model.attributes.pages, p => p.id)
		this.listenTo(Backbone, `move:page`, ({ rootFormId, pageOrder }) => {
			if (rootFormId == this.model.get('rootFormId')) {
				this.pageOrder = pageOrder
			}
		})
	},

	render: function() {
		this.$el.empty()
		_.invokeMap(this.widgetViews, 'remove')

		// #52578 hide field widgets from non-admins that use "hide from
		// non-admin" fields
		const widgetsToRender = _.filter(this.model.get('widgets'), w => {
			if (w.type === FIELD) return !isFieldHiddenForUser(w.fieldId, this.model.dataset)
			return true
		})

		// Iterate through each widgetsToRender widget and reassign the statements returned
		// from getValidWidgetStatements to the widget's statements property
		_.each(widgetsToRender, widget => {
			widget.statements = getValidWidgetStatements(widget.statements, this.model)
		})

		const isModifyRecords = this.model.dataset.isModifyRecords()
		const potentialSaveWidgets = !isModifyRecords ?
			getWidgetsWithPotentialSaveAction(widgetsToRender) : []
		const statementsToCheck = []
		this.widgetViews = _.map(widgetsToRender, (widget, i) => {
			let view = this.createWidgetView(widget)

			// if a user can't see any part of a record-list, we won't add the
			// view, so there are cases where the view can be undefined here
			if (!_.isNil(view)) {
				this.$el.append(view.el)
				this.renderWidgetView(view)
			}
			if (_.includes(potentialSaveWidgets, widget) && !isModifyRecords) {
				const newWidget = _.extend({}, widget, { state: DISABLED })
				statementsToCheck.push({
					statements: widget.statements,
					callBack: () => this.replaceWidgetView(newWidget, i)
				})
			}
			return view
		})
		this.checkSavePossible(statementsToCheck)

		this.widgetViews = _.filter(this.widgetViews, v => !_.isNil(v))
		if (!this.allowInput && !isModifyRecords) {
			// #47198 all users who can see a form should be able to click
			// buttons that only have go-to-page actions. Filter out the button
			// widgets here so that they are enabled until we know what they do
			const filter = this.$(':not(.find-input, .actual-button)')
			disableControls(this.el, { filter })
		}
		this.delegateEvents()
		return this
	},

	createWidgetView: function(widget) {
		if (widget.type === RECORD_LIST) {
			// if there are no valid columns on the record list, don't render it
			const validColumns = _.filter(widget.columns, c =>
				!isFieldHiddenForUser(c.fieldId, this.model.dataset))
			if (_.isEmpty(validColumns)) return
		}
		let constructor = widgetMetadata.views[widget.type]
		let descriptionHtml = escapePreserveLineBreaks(widget.description)
		if (!_.isEmpty(descriptionHtml)) {
			descriptionHtml = urlsToLinks(descriptionHtml)
		}
		let view = new constructor({
			widget: _.extend({}, widget, { descriptionHtml }),
			model: this.model,
			formView: this.formView,
			formDirector: this.options.formDirector,
			allowInput: this.allowInput,
			treeItemsById: this.options.treeItemsById
		})
		view.$el
			.addClass(`form-widget is-${widget.state}`)
			.data('widget', widget)
			.data('widgetId', widget.id)
		return view
	},

	renderWidgetView: function(view) {
		view.render()
		let state = view.options.widget.state
		let controls = view.$('input, select, button, textarea')
		// Don't allow tabbing to disabled/hidden controls
		if (_.includes([DISABLED, HIDDEN], state)) {
			controls.prop('tabindex', -1)
		}
		// AFTER the view is rendered so the view doesn't remove the
		// controls
		if (!this.allowInput) {
			view.$el.append(formWidgetCommonControls())
		}

		this.listenTo(view, 'change', () => {
			this.trigger('change', {
				widgetId: view.options.widget.id
			})
		})
	},

	replaceWidgetView: function(widget, index) {
		let prevView = this.widgetViews[index]
		let newView = this.createWidgetView(widget)

		// replace the old widget with the new one
		this.widgetViews.splice(index, 1, newView)
		this.$(prevView.el).replaceWith(newView.el)
		prevView.remove()

		this.renderWidgetView(newView)
	},

	updateWidgetView: function(widget) {
		const widgetIndex = _.findIndex(this.widgetViews, v =>
			v.options.widget.id === widget.id)
		if (widgetIndex === -1) return
		this.replaceWidgetView(widget, widgetIndex)
	},

	onClickEdit: function(e) {
		let index = this.getWidgetIndex(e)
		let widget = this.model.get('widgets')[index]
		let constructor = widgetMetadata.editors[widget.type]
		let view = new constructor({
			model: this.model,
			widget,
			pageOrder: this.pageOrder
		})

		appDisplay.dialog(view)

		view.on('done', newData => {
			_.extend(widget, newData)
			// #53952 if a field widget has been edited so that it
			// no longer has a valueLabelFieldId (ie was changed from
			// using a field from a linked dataset to using the main
			// dataset), we want to clear that property from the widget
			if (_.isNil(_.get(newData, 'valueLabelFieldId'))) {
				_.unset(widget, 'valueLabelFieldId')
			}
			this.replaceWidgetView(widget, index)
			this.trigger('edit', widget)
		})
	},

	checkSavePossible: function(statementsToCheck) {
		if (_.isEmpty(statementsToCheck)) return

		const statements = _.map(statementsToCheck, s => s.statements)
		const actor = new EvaluateStatementsActor({
			model: this.model,
			statements,
			formDirector: this.options.formDirector,
			formView: this.formView
		})
		actor.check().then(results => {
			_.each(results, (r, i) => {
				if (!!r) statementsToCheck[i].callBack()
			})
		})
	},

	onClickDelete: function(e) {
		let index = this.getWidgetIndex(e)
		let widget = this.model.get('widgets').splice(index, 1)[0]

		// remove any actions that reference the deleted widget. This will
		// be done on the server, but we need to do it here as well before the
		// user clicks save
		const { id, tempId } = widget
		const newStatements = filterInvalidWidgetReferences({
			statements: _.cloneDeep(this.model.get('statements')),
			deletedId: id,
			deletedTempId: tempId
		})
		this.model.set('statements', newStatements)

		const widgets = this.model.get('widgets')
		_.each(widgets, w => {
			w.statements = filterInvalidWidgetReferences({
				statements: w.statements,
				deletedId: id,
				deletedTempId: tempId
			})
		})

		// #57581
		// Store the scroll position of the scrollable parent element,
		// `widgets-view-container`, so that we can restore it after the view
		// is re-rendered
		let scrollPosition = this.$el.parent().scrollTop()

		this.render()

		// Restore the scroll position
		this.$el.parent().scrollTop(scrollPosition)

		this.trigger('delete', widget)
	},

	getWidgetIndex: function(e) {
		return this.$(e.target).closest('.form-widget').index()
	},

	onDisplayActionsDone () {
		this.widgetViews.forEach(view => {
			view.onDisplayActionsDone?.()
		})
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap(this.widgetViews, 'remove')
	}

})

const getWidgetsWithPotentialSaveAction = (widgets) => {
	return _.filter(widgets, w => {
		if (w.type != BUTTON) return false

		// we already checked statements that are guaranteed to save
		if (isSaveGuaranteed(w.statements)) return false

		// return those where a save is not guaranteed but possible
		return _.some(_.flatten(w.statements), s =>
			_.some(s.consequences, c => c.type == SAVE_RECORD))
	})
}

/**
 * removes actions that reference a deleted widget
 * then removes statements that no longer have any valid actions after doing so
 * then removes statement groups that no longer have any statements after that
 */
const filterInvalidWidgetReferences = ({ statements, deletedId, deletedTempId }) => {
	statements = _.map(statements, sg => {
		_.each(sg, s => {
			s.consequences = _.filter(s.consequences, c => {
				const { changeWidgetId, changeWidgetTempId } = c
				const idRef = !_.isNil(changeWidgetId)
				const tempIdRef = !_.isNil(changeWidgetTempId)

				// if the action doesn't change a widget, then it is valid
				if (!idRef && !tempIdRef) return true

				// if the action had a change id and the deleted widget had an
				// id, remove the action if they don't match
				if (idRef && !_.isNil(deletedId)) {
					return changeWidgetId != deletedId
				}

				// if the action had a change tempId and the deleted widget had
				// a tempId, remove the action if they don't match
				if (tempIdRef && !_.isNil(changeWidgetTempId)) {
					return changeWidgetTempId != deletedTempId
				}

				// otherwise, there was a tempId and an id, so the action didn't
				// reference the deleted widget
				return true
			})
		})
		return _.filter(sg, (s, i) =>  {
			return i == _.size(sg) - 1 ? true :
				!_.isEmpty(s.consequences) || !_.isEmpty(s.conditions)
		})
	})
	return _.filter(statements, sg =>
		_.some(sg, s => !_.isEmpty(s.consequences) ||
			!_.isEmpty(s.conditions)))
}

/**
 * Checks widget statements validity based on whether fields still exist.
 * This is meant primarily to handle cases where a dataset field is deleted.
 * @param statementGroup - an array of statements tied to a widget
 * @param model - the form model
 * @returns {Array} - an array of valid statements or an empty array if no valid statements exist
 */
function getValidWidgetStatements(statementGroup, model) {
	// Check to see what fields exist in the dataset
	const fieldsById = model?.dataset?.combinedFieldsById

	// Runs through each statement in the group and checks whether the fieldId
	// exists in the dataset. If it does, it adds it to the map; otherwise, an
	// empty array is added to mark for removal
	const filteredStatements = _.map(statementGroup, statements =>
		_.map(statements, statement => {
			const conditions = _.filter(statement?.conditions, cd => _.has(fieldsById, cd?.fieldId))
			const consequences = _.filter(statement?.consequences, cq =>
				cq?.type !== SET_FIELD_VALUE || _.has(fieldsById, cq?.setFieldValueFieldId)
			)

			// We don't want to add:
			// 1. Statements without conditions AND consequences
			// 2. Statements WITH a statement type but no conditions
			// 3. Statements that have no consequences
			if ((_.isEmpty(conditions) && _.isEmpty(consequences)) ||
				(statement?.statementType && _.isEmpty(conditions)) ||
				_.isEmpty(consequences)
			) {
				return []
			}

			const result = {
				conditions,
				consequences
			}

			// Only add a statementType if one exists to avoid attaching it to
			// pure action statements
			if (statement?.statementType) {
				result.statementType = statement.statementType
			}

			return result
		})
	)

	// Filter out empty statements
	const finalStatements = _.map(filteredStatements, statements =>
		_.filter(statements, statement => !_.isEmpty(statement))
	)

	// For conditionals to display properly, some array must exist at the end of the list
	// Therefore, check if all statements are conditionals and append an empty statement
	_.each(finalStatements, statements => {
		if (_.every(statements, statement => statement?.statementType)) {
			statements.push({
				conditions: [],
				consequences: []
			})
		}
	})

	return _.isEmpty(_.flattenDeep(finalStatements)) ?
		[] : finalStatements
}

_.extend(WidgetsView, {
	filterInvalidWidgetReferences,
	getWidgetsWithPotentialSaveAction,
	getValidWidgetStatements
})
export default WidgetsView
export { filterInvalidWidgetReferences }
export { getWidgetsWithPotentialSaveAction }
export { getValidWidgetStatements }
WidgetsView.__filename__ = 'forms/widgets/widgets-view.js'
